import ProductSubscribedCounterSignatureResponseResource from "common/resources/ProductSubscribed/ProductSubscribedCounterSignatureResponseResource";
import ProductSubscribedTransferDossierRequestResource from "common/resources/ProductSubscribed/ProductSubscribedTransferDossierRequestResource";
import { ValidationError } from "common/resources/Resource";
import Button, { EButtonVariant } from "components/elements/Button";
import MessageBox from "components/elements/MessageBox";
import Modal from "components/elements/Modal";
import Typography, { ITypo, ITypoColor, ITypoComponent } from "components/elements/Typography";
import Form from "components/materials/Form";
import InputElement, { EInputType } from "components/materials/Form/InputElement";
import I18n from "components/materials/I18n";
import { FormEvent, useCallback, useState } from "react";
import ProductSubscribedService from "services/ProductSubscribedService";
import { container } from "tsyringe";

import classes from "./classes.module.scss";

type IProps = {
	isOpen: boolean;
	action?: "transfer" | "prepare";
	onClose: () => void;
	productSubscribedId: string | null;
	/** Demande en cours auprès de la société de gestion : la transmission la remplace, ses coordonnées sont reprises. */
	pendingRequest?: ProductSubscribedCounterSignatureResponseResource | null;
	onTransferred: (signatory: ProductSubscribedTransferDossierRequestResource) => void;
};

const productSubscribedService = container.resolve(ProductSubscribedService);

/**
 * Transmission d'un dossier de private equity : le contrat signé par l'investisseur part en signature chez la personne indiquée,
 * qui signe pour la société de gestion. Sa signature valide le dossier.
 */
export default function TransferDossierModal({ isOpen, onClose, productSubscribedId, pendingRequest, onTransferred, action = "transfer" }: IProps) {
	const copy = I18n.asset.component.transfer_dossier_modal;

	return (
		<Modal isOpen={isOpen} onClose={onClose} className={classes["modal"]}>
			<div className={classes["root"]}>
				<div className={classes["heading"]}>
					<Typography typo={ITypo.H1} component={ITypoComponent.H2}>
						{action === "prepare" ? "Préparer les signatures" : copy.title}
					</Typography>
					<Typography typo={ITypo.P_MEDIUM} color={ITypoColor.WILD_SAND_700}>
						{action === "prepare"
							? "Renseignez le représentant IQ-EQ avant l’envoi à l’investisseur. Il complétera sa fonction lors de sa contre-signature. Son lien ne sera envoyé qu’après votre action Transmettre."
							: pendingRequest?.sequential
								? "Le bulletin est prêt pour le représentant IQ-EQ indiqué ci-dessous. Transmettre lui envoie son lien personnel de contre-signature et les pièces du dossier."
								: copy.description}
					</Typography>
				</div>

				{pendingRequest && !pendingRequest.sequential && action === "transfer" && (
					<MessageBox type="info" text={I18n.trslt(copy.replace, { name: `${pendingRequest.firstName} ${pendingRequest.lastName}` })} />
				)}

				{/* La fenêtre démonte son contenu à la fermeture : le formulaire repart des coordonnées connues à chaque ouverture. */}
				{productSubscribedId && (
					<TransferDossierForm
						action={action}
						productSubscribedId={productSubscribedId}
						pendingRequest={pendingRequest}
						onClose={onClose}
						onTransferred={onTransferred}
					/>
				)}
			</div>
		</Modal>
	);
}

type IFormProps = Pick<IProps, "pendingRequest" | "onClose" | "onTransferred" | "action"> & { productSubscribedId: string };

function TransferDossierForm({ productSubscribedId, pendingRequest, onClose, onTransferred, action = "transfer" }: IFormProps) {
	const copy = I18n.asset.component.transfer_dossier_modal;
	const [errors, setErrors] = useState<ValidationError[]>([]);
	const [isSubmitting, setIsSubmitting] = useState(false);

	const submit = useCallback(
		async (_event: FormEvent<HTMLFormElement>, formData: { [key: string]: unknown }) => {
			const text = (name: string) => String(formData[name] ?? "").trim();
			const signatory = ProductSubscribedTransferDossierRequestResource.hydrate<ProductSubscribedTransferDossierRequestResource>({
				firstName: text("firstName"),
				lastName: text("lastName"),
				email: text("email").toLowerCase(),
				phoneNumber: text("phoneNumber"),
			});

			setErrors([]);
			setIsSubmitting(true);
			try {
				await signatory.validateOrReject();
				if (action === "transfer") await productSubscribedService.transferDossier(productSubscribedId, signatory);
				else await productSubscribedService.prepareHexaSignatures(productSubscribedId, signatory);
				onTransferred(signatory);
			} catch (error) {
				if (Array.isArray(error)) setErrors(error as ValidationError[]);
			} finally {
				setIsSubmitting(false);
			}
		},
		[onTransferred, productSubscribedId, action],
	);

	// Refus qui ne tient pas à un champ (état du dossier, service de signature) : affiché en clair au-dessus des boutons.
	const refusal = errors.find((error) => error.property === "form")?.constraints?.["error"];

	return (
		<Form className={classes["form"]} errors={errors.filter((error) => error.property !== "form")} onSubmit={submit}>
			<div className={classes["names"]}>
				<InputElement
					name="firstName"
					type={EInputType.TEXT}
					label={copy.first_name}
					defaultValue={pendingRequest?.firstName}
					readonly={action === "transfer" && pendingRequest?.sequential}
					autoComplete="off"
				/>
				<InputElement
					name="lastName"
					type={EInputType.TEXT}
					label={copy.last_name}
					defaultValue={pendingRequest?.lastName}
					readonly={action === "transfer" && pendingRequest?.sequential}
					autoComplete="off"
				/>
			</div>
			<InputElement
				name="email"
				type={EInputType.TEXT}
				label={copy.email}
				caption={copy.email_caption}
				defaultValue={pendingRequest?.email}
				readonly={action === "transfer" && pendingRequest?.sequential}
				autoComplete="off"
			/>
			<InputElement
				name="phoneNumber"
				type={EInputType.TEXT}
				label={copy.phone}
				caption={copy.phone_caption}
				placeholder="06 12 34 56 78"
				defaultValue={pendingRequest?.phoneNumber}
				readonly={action === "transfer" && pendingRequest?.sequential}
				autoComplete="off"
			/>

			{refusal && <MessageBox type="warning" text={refusal} />}

			<div className={classes["buttons"]}>
				<Button variant={EButtonVariant.OUTLINED} onClick={onClose} disabled={isSubmitting}>
					{I18n.asset.common.cancel}
				</Button>
				<Button type="submit" loading={isSubmitting} disabled={isSubmitting}>
					{action === "prepare" ? "Envoyer à l’investisseur" : copy.submit}
				</Button>
			</div>
		</Form>
	);
}
